import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import { fetchAuthMe, logoutFetch } from "../../../store/authSlice";

import { getLoginPath } from "../../../utils/authRedirect";

import { NAV_ICONS, IconBell, IconSettings } from "../../ui/icons";

import clinicAPI from "../../../services/clinicApi";
import clubSupportAPI from "../../../services/clubSupportApi";

import { countPendingPolls } from "../../../utils/clientSessionUtils";

import { givenNameFromFullName } from "../../../utils/clientAppUtils";

import { useClientAthlete } from "../../../context/ClientAthleteContext";

import ClientSplash from "./ClientSplash";

import ClientPushBanner from "./ClientPushBanner";

import ManageDesktopShell from "../common/ManageDesktopShell";

import NotificationsDropdown from "../common/NotificationsDropdown";

import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import { resolveSplashBranding } from "../../../utils/splashBranding";
import { buildClientNav, redirectForClientRoute } from "../../../utils/clientNav";

import { usePwaChrome, resolvePwaChromeConfig } from "../../../utils/pwaThemeColor";

import {

  usePullToRefresh,

  PullToRefreshIndicator,

} from "../../../hooks/usePullToRefresh";

import { dispatchClientRefresh } from "../../../hooks/useClientRefresh";

import { useIsMobile } from "../../../hooks/useMediaQuery";
import { usePortalHtmlClass } from "../../../hooks/usePortalHtmlClass";

import { useSkipPwaSplash, useShowPwaBanners, useStandalone } from "../../../hooks/useStandalone";

import {

  isPushSupported,

  registerServiceWorker,

  syncPushSubscriptionIfGranted,

} from "../../../utils/pushNotifications";

import L from "../../../constants/baysportLabels";

import desk from "./clientDesktop.module.css";

import s from "./clientApp.module.css";



const PAGE_TITLES = {

  "/app": null,

  "/app/calendar": "Календарь",

  "/app/chat": "Сообщения",

  "/app/life": "Клуб",

  "/app/profile": "Профиль",

  "/app/stats": "Рейтинг",

  "/app/notifications": "Уведомления",

  "/app/members": "Участники",

};



const SELF_HEADER = [

  "/app",

  "/app/calendar",

  "/app/life",

  "/app/profile",

  "/app/stats",

  "/app/chat",

];



function todayLabel() {

  return new Date().toLocaleDateString("ru-RU", {

    weekday: "long",

    day: "numeric",

    month: "long",

  });

}



function isSelfHeaderPath(base) {

  return SELF_HEADER.some(

    (path) => base === path || base.startsWith(`${path}/`),

  );

}



function resolvePageTitle(base) {

  if (PAGE_TITLES[base]) return PAGE_TITLES[base];

  if (base.startsWith("/app/profile/")) return "Профиль";

  if (base.startsWith("/app/members/")) return "Профиль участника";

  if (base.startsWith("/app/chat/")) return "Сообщения";

  return null;

}



export default function ClientAppShell({ children }) {

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { pathname } = useLocation();

  const { tenant, profile, entitlements, billing } = useSelector((state) => state.auth);

  const nav = useMemo(
    () => buildClientNav(entitlements, tenant, billing),
    [entitlements, tenant, billing],
  );

  const {

    athleteQueryParam,

    activeAthleteId,

    activeAthlete,

    isParentMode,

  } = useClientAthlete();

  const [pollBadge, setPollBadge] = useState(0);

  const [chatBadge, setChatBadge] = useState(0);

  const [notifyUnread, setNotifyUnread] = useState(0);

  const [notifyOpen, setNotifyOpen] = useState(false);

  const notifyBtnRef = useRef(null);

  const skipSplash = useSkipPwaSplash();

  const showPwaBanners = useShowPwaBanners();

  const isMobile = useIsMobile(768);
  const standalone = useStandalone();

  const [splashDone, setSplashDone] = useState(skipSplash);

  const [swReady, setSwReady] = useState(false);

  useEffect(() => {
    if (splashDone && !entitlements) {
      dispatch(fetchAuthMe());
    }
  }, [splashDone, entitlements, dispatch]);

  const splashForcedRef = useRef(false);

  const splashBranding = useMemo(
    () => resolveSplashBranding({ tenant, features: entitlements?.features }),
    [tenant, entitlements?.features],
  );

  useEffect(() => {
    const to = redirectForClientRoute(pathname, entitlements, tenant, billing);
    if (to) navigate(to, { replace: true });
  }, [pathname, entitlements, tenant, billing, navigate]);



  const handleSplashComplete = useCallback(() => {

    splashForcedRef.current = true;

    setSplashDone(true);

  }, []);



  useEffect(() => {

    const safety = window.setTimeout(() => {

      if (!splashForcedRef.current) setSplashDone(true);

    }, 5000);

    return () => window.clearTimeout(safety);

  }, []);



  const badgeGenRef = useRef(0);
  const loadBadge = useCallback(async () => {
    const gen = ++badgeGenRef.current;
    try {
      const [polls, chatUnread, notif, support] = await Promise.all([
        clinicAPI.getMyTrainingPolls(athleteQueryParam).catch(() => ({ polls: [] })),
        clinicAPI
          .getMyChatUnread({})
          .catch(() => ({ unread_count: 0 })),
        clinicAPI
          .getMyNotifications(athleteQueryParam)
          .catch(() => ({ unread_count: 0 })),
        clubSupportAPI.getSupportUnread().catch(() => ({ unread: 0 })),
      ]);
      if (gen !== badgeGenRef.current) return;
      setPollBadge(countPendingPolls(polls.polls));
      setChatBadge(
        (chatUnread.unread_count || 0) + (Number(support.unread) || 0),
      );
      setNotifyUnread(notif.unread_count || 0);
    } catch {
      if (gen !== badgeGenRef.current) return;
      setPollBadge(0);
      setChatBadge(0);
      setNotifyUnread(0);
    }
  }, [athleteQueryParam]);



  useEffect(() => {

    loadBadge();

    const id = setInterval(loadBadge, 30_000);

    const onActivity = () => loadBadge();

    window.addEventListener("baysport:chat-activity", onActivity);

    window.addEventListener("baysport:poll-activity", onActivity);

    window.addEventListener("baysport:support-activity", onActivity);

    return () => {

      clearInterval(id);

      window.removeEventListener("baysport:chat-activity", onActivity);

      window.removeEventListener("baysport:poll-activity", onActivity);

      window.removeEventListener("baysport:support-activity", onActivity);

    };

  }, [loadBadge, pathname, activeAthleteId]);



  useEffect(() => {

    const onVis = () => {

      if (document.visibilityState === "visible") {

        dispatchClientRefresh();

      }

    };

    document.addEventListener("visibilitychange", onVis);

    return () => document.removeEventListener("visibilitychange", onVis);

  }, []);



  useEffect(() => {

    if (!splashDone) return undefined;

    const timer = window.setTimeout(() => {

      registerServiceWorker().then(() => {

        setSwReady(true);

        if (isPushSupported()) syncPushSubscriptionIfGranted();

      });

    }, 1500);

    return () => window.clearTimeout(timer);

  }, [splashDone]);



  usePortalHtmlClass("client");



  useEffect(() => {
    setNotifyOpen(false);
  }, [pathname]);

  const base = pathname.replace(/\/$/, "") || "/app";

  const selfHeader = isSelfHeaderPath(base);

  const isHome = base === "/app";

  const isCalendar = base === "/app/calendar";

  const isChat = base === "/app/chat" || base.startsWith("/app/chat/");

  const isLife = base === "/app/life";

  const isStats = base === "/app/stats";

  const isProfile =
    base === "/app/profile" ||
    base.startsWith("/app/profile/") ||
    base.startsWith("/app/members/");

  const pageTitle = resolvePageTitle(base);

  const notifyBadgeCount = notifyUnread;



  const firstName = givenNameFromFullName(

    isParentMode && activeAthlete ? activeAthlete.full_name : profile?.full_name,

  );



  const { shellStyle, theme, themeMode } = useClubPortalBranding("ca", null, true, splashDone);

  const immersiveCover =
    base === "/app" || base === "/app/life" || base === "/app/profile";

  const pwaChrome = resolvePwaChromeConfig({
    portal: "client",
    themeMode,
    theme,
    immersive: immersiveCover,
  });

  usePwaChrome(pwaChrome.color, {
    enabled: splashDone && Boolean(theme) && (isMobile || standalone),
    isDark: pwaChrome.isDark,
    immersive: pwaChrome.immersive,
    themeColor: pwaChrome.themeColor,
  });



  const handlePullRefresh = useCallback(async () => {

    dispatchClientRefresh();

    await new Promise((r) => setTimeout(r, 400));

  }, []);



  const { pulling, pullDistance } = usePullToRefresh(handlePullRefresh, {

    enabled: splashDone && isMobile,

  });



  const logout = () => {

    dispatch(logoutFetch());

    navigate(getLoginPath("client"));

  };



  const navBadge = (item) => {
    if (item.id === "home") return pollBadge;
    if (item.id === "chat") return chatBadge;
    return 0;
  };



  const pageContent = (

    <div key={`${pathname}-${activeAthleteId || "self"}`} className={s.pageEnter}>

      {children}

    </div>

  );



  if (!isMobile && !standalone && splashDone) {
    return (
      <>
        <ManageDesktopShell
          homeTo="/app"
          sections={[{ items: nav }]}
          profile={profile}
          profileTo="/app/profile"
          chatBadge={chatBadge}
          notifyBadge={notifyUnread}
          notifyOpen={notifyOpen}
          onNotifyToggle={() => setNotifyOpen((v) => !v)}
          notifyBtnRef={notifyBtnRef}
          pageTitle={isProfile || isLife || isStats ? null : pageTitle}
          isOverview={false}
          isSchedule={isCalendar}
          isChat={isChat}
          isJournal={false}
          isLife={isLife}
          isProfile={isProfile}
          isSettings={false}
          flushContent={isChat || isProfile || isLife}
          shellStyle={shellStyle}
          brandingTheme={theme}
          showSearch={false}
        >
          <div
            key={pathname}
            className={`${s.pageEnter} ${desk.desktopRoot}${
              isCalendar || isChat ? ` ${desk.desktopWide}` : ""
            }`}
          >
            {children}
          </div>
        </ManageDesktopShell>
        <NotificationsDropdown
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          anchorRef={notifyBtnRef}
          allPath="/app/notifications"
          portal="client"
          athleteId={activeAthleteId || ""}
          onMarkedRead={loadBadge}
        />
      </>
    );
  }



  return (

    <>

      {!splashDone ? (

        <ClientSplash branding={splashBranding} onComplete={handleSplashComplete} />

      ) : null}



      <div
        className={`${s.shell} ${splashDone ? "" : s.shellBehindSplash}`}
        style={splashDone ? shellStyle : undefined}
      >

        <PullToRefreshIndicator pulling={pulling} pullDistance={pullDistance} />

        {splashDone && swReady && showPwaBanners ? <ClientPushBanner /> : null}

        <main className={selfHeader ? s.mainHome : s.main} data-pull-scroll>

          {pageContent}

        </main>



        <nav

          className={`${s.tabBar} ${splashDone ? "" : s.tabBarHidden}`}

          aria-label="Навигация"

        >

          {nav.map((item) => {

            const Icon = NAV_ICONS[item.icon] || NAV_ICONS.overview;

            const active = item.end

              ? base === item.to

              : base === item.to || base.startsWith(`${item.to}/`);

            const badge = navBadge(item);

            return (

              <Link

                key={item.to}

                to={item.to}

                end={item.end}

                className={active ? `${s.tab} ${s.tabActive}` : s.tab}

                aria-current={active ? "page" : undefined}

              >

                <span className={s.tabIconWrap}>

                  <Icon className={s.tabIcon} />

                  {badge > 0 ? (

                    <span className={s.tabBadge}>{badge > 9 ? "9+" : badge}</span>

                  ) : null}

                </span>

                <span className={s.tabLabel}>{item.label}</span>

              </Link>

            );

          })}

        </nav>

      </div>

    </>

  );

}


